import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/apiClient";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined=loading, null=guest, obj=user
  const [error, setError] = useState("");

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("Auth me check failed:", err.message);
      }
      setUser(null);
    }
  }, []);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const requestEmailLoginCode = async (email) => {
    setError("");
    try {
      const { data } = await api.post("/auth/email-login/request", { email });
      return data;
    } catch (e) {
      setError(formatApiError(e));
      return null;
    }
  };

  const loginWithEmailCode = async (email, code) => {
    setError("");
    try {
      const { data } = await api.post("/auth/email-login/verify", { email, code });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const register = async (name, email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const requestRegistration = async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/registration/request", payload);
      return data;
    } catch (e) {
      setError(formatApiError(e));
      return null;
    }
  };

  const verifyRegistration = async (email, code) => {
    setError("");
    try {
      const { data } = await api.post("/auth/registration/verify", { email, code });
      setUser(data);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const resendRegistration = async (email) => {
    setError("");
    try {
      const { data } = await api.post("/auth/registration/resend", { email });
      return data;
    } catch (e) {
      setError(formatApiError(e));
      return null;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed:", err?.message || err);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, requestEmailLoginCode, loginWithEmailCode, register, requestRegistration, verifyRegistration, resendRegistration, logout, refreshMe, setError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
